import { yupResolver } from '@hookform/resolvers/yup';
import { Divider, Typography } from '@mui/material';
import { useAtom, useSetAtom } from 'jotai';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { cloneDeep } from 'lodash-es';
import { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FormFactoryWithStandardLayout } from '../../../components/Form/FormFactory';
import { FieldEntity, IField } from '../../../data-models/field2.data-model';
import { FDMap } from '../../../util/data-structure/FDMap';
import { MaggieFeatureFlags } from '../../../util/feature-flags';
import { captureFormValidationError } from '../../../util/SentryUtils';
import { createForm } from '../../../view-models/form.view-model';
import { StepperFormButtons } from '../../Finance2/Forms/StepperFormButtons';
import { useCustomFieldsActions } from '../CustomFields/hooks/useCustomFieldsActions';
import { fromFieldViewModel } from './custom-field-form.view-model';
import { CustomFieldFormType, customFieldFormSchema } from './CustomField.schema';
import { customFieldFormCurrentStepStateJ, CustomFieldFormDataState } from './CustomFieldFormStateJ';
import { isEditMode, useCustomFieldsStep2Fields } from './customFieldFormUtils';
import { CustomFieldsFormContainer } from './CustomFieldsFormContainer';

export function CustomFieldFormStep2() {
  const [formData, setFormData] = useAtom(CustomFieldFormDataState);
  const setStep = useSetAtom(customFieldFormCurrentStepStateJ);
  const { createField, updateField } = useCustomFieldsActions();
  const editMode = Boolean(formData) && isEditMode(formData!);
  const { allowFreeFormMultiSelect } = useFlags<MaggieFeatureFlags>();

  const methods = useForm<CustomFieldFormType>({
    defaultValues: cloneDeep(formData!),
    mode: 'onChange',
    resolver: yupResolver(customFieldFormSchema(false, allowFreeFormMultiSelect)),
  });

  const handlePrevious = async () => {
    setFormData(methods.getValues());
    setStep((prev) => prev - 1);
    return true;
  };

  const handleSubmit = async () => {
    const isValid = await methods.trigger();
    if (isValid) {
      const payload = fromFieldViewModel(methods.getValues() as CustomFieldFormType & IField<unknown>);
      if (editMode) {
        await updateField(payload);
      } else {
        await createField(payload);
      }
      return true;
    } else {
      captureFormValidationError('Custom fields step 2', methods.formState.errors);
      return false;
    }
  };

  if (!formData) {
    return <>No form data</>;
  }

  return (
    <FormProvider {...methods}>
      <CustomFieldsFormContainer>
        <Typography>Set Field Display</Typography>
        <FormStep2 entity={formData!.entity as FieldEntity} />
      </CustomFieldsFormContainer>
      <StepperFormButtons
        stepIsValid={handleSubmit}
        stepState={customFieldFormCurrentStepStateJ}
        handleGoBack={handlePrevious}
        nextButtonLabel={editMode ? 'Update' : 'Create'}
      />
    </FormProvider>
  );
}

function FormStep2({ entity }: { entity: FieldEntity }) {
  const fields = useCustomFieldsStep2Fields(entity);
  const form1 = useMemo(
    () =>
      createForm({
        fields: FDMap.fromArray(
          fields.filter((f) => formRefFields.has(f.key)),
          'key'
        ),
      }),
    [fields]
  );

  const form2 = useMemo(
    () =>
      createForm({
        fields: FDMap.fromArray(
          fields.filter((f) => !formRefFields.has(f.key)),
          'key'
        ),
      }),
    [fields]
  );

  return (
    <>
      <FormFactoryWithStandardLayout form={form1} />
      <Divider />
      <FormFactoryWithStandardLayout form={form2} />
    </>
  );
}

const formRefFields = new Set([
  `_viewModel.addingCaptableSecurity`,
  `_viewModel.addingTransaction`,
  `_viewModel.addingDeal`,
  `_viewModel.addingRound`,
  `_viewModel.companyLists`,
  `_viewModel.companyProfile`,
]);
